import { skipToken } from "@reduxjs/toolkit/query";
import {
  IntegrationProvider,
  type GenesysIntegration,
} from "@repo/app-types/entities/integration";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { FC } from "react";
import { Navigate, Outlet, useOutletContext } from "react-router";
import {
  useGenesysFailedImportsCountQuery,
  useGenesysIntegration,
} from "../../../api/entities/genesysIntegration.js";
import { appPaths } from "../../../app/appPaths.js";
import {
  PageBody,
  TabbedPageLayout,
} from "../../../components/layout/pageLayout.js";
import {
  RouteTabs,
  type RouteTabItem,
} from "../../../components/layout/routeTabs.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { useSettingsSectionsPolicy } from "../settingsSectionsPolicy.js";
import { IntegrationLogo } from "./integrationLogo.js";

const GENESYS_TITLE = "Genesys Cloud";

/**
 * What the Settings and Failed imports tabs share: the Genesys integration and
 * its load state. Fetched once by the section shell and handed down through the
 * router `Outlet` so the tabs don't each re-subscribe.
 */
export interface GenesysDetailOutletContext {
  integration: GenesysIntegration | null;
  isLoading: boolean;
  isError: boolean;
  onReload: () => void;
}

export function useGenesysDetailOutletContext(): GenesysDetailOutletContext {
  return useOutletContext<GenesysDetailOutletContext>();
}

/**
 * The Genesys integration detail page: a wider, tabbed frame (like
 * `/settings/teammates`) hosting a **Settings** tab (the connection UI) and a
 * **Failed imports** tab (the customer's own failed `genesys_recording` rows).
 * The other integrations keep the narrow, non-tabbed detail rendered by
 * `IntegrationSettingsSection`; only Genesys routes here, via its own literal
 * `genesys` route, so it alone widens and grows tabs.
 *
 * The tab strip only appears once a connection exists — before that there is
 * nothing to show failed imports for, so the page is just the connection form.
 */
export const GenesysIntegrationDetailSection: FC = () => {
  const isGenesysEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.GenesysIntegration,
  );
  const { usesRecruitingIntegrations } = useSettingsSectionsPolicy();

  // Recruiting companies get the recruiting integrations page and never have
  // Genesys; gate the fetch (and redirect below) the same way the shared
  // section's visibility does.
  const isAvailable = isGenesysEnabled && !usesRecruitingIntegrations;
  const genesysStatus = useGenesysIntegration(isAvailable);
  const integration = genesysStatus.currentData?.integration ?? null;
  const isConnected = integration !== null;

  // The tab badge count. Only meaningful once connected (the tabs only show
  // then), so it is skipped until then.
  const failedCount = useGenesysFailedImportsCountQuery(
    isConnected ? undefined : skipToken,
  );

  if (!isAvailable) {
    return <Navigate to={appPaths.settingsIntegrations.build()} replace />;
  }

  const count = failedCount.data?.count ?? 0;
  const tabs: RouteTabItem[] = [
    { label: "Settings", to: "." },
    {
      label:
        count > 0
          ? `Failed imports (${count.toLocaleString()})`
          : "Failed imports",
      to: "failed-imports",
    },
  ];

  const context: GenesysDetailOutletContext = {
    integration,
    isLoading: !genesysStatus.currentData && !genesysStatus.isError,
    isError: genesysStatus.isError && !genesysStatus.currentData,
    onReload: () => void genesysStatus.refetch(),
  };

  return (
    <TabbedPageLayout
      header={
        <PageHeader
          width={PageWidth.Wide}
          title={
            <span className="flex min-w-0 items-center gap-2">
              <IntegrationLogo provider={IntegrationProvider.GENESYS} />
              <span className="truncate">{GENESYS_TITLE}</span>
            </span>
          }
          tabs={
            isConnected ? (
              <RouteTabs ariaLabel="Genesys integration sections" tabs={tabs} />
            ) : undefined
          }
        />
      }
    >
      <PageBody width={PageWidth.Wide}>
        <Outlet context={context} />
      </PageBody>
    </TabbedPageLayout>
  );
};
